import { useState } from 'react';
import { User, FileText, MapPin, Calendar, ChevronDown, ChevronUp, Shield } from 'lucide-react';
import { cn } from '@/lib/utils';
import { InfoGrid, InfoItem } from '../shared/InfoGrid';
import { formatPassengerType, formatGender, formatDateGerman } from '../utils/formatters';
import type { Traveler, RegulatoryDetail } from '../utils/types';

interface TravelerCardProps {
  traveler: Traveler;
  className?: string;
}

export function TravelerCard({ traveler, className }: TravelerCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const primaryName = traveler.names[0];
  const fullName = primaryName
    ? `${primaryName.title ? primaryName.title + ' ' : ''}${primaryName.firstName} ${primaryName.lastName}`
    : 'Unknown';

  const hasRegulatoryDetails = traveler.regulatoryDetails && traveler.regulatoryDetails.length > 0;

  return (
    <div
      className={cn(
        'rounded-lg border border-slate-200 bg-white shadow-sm',
        'transition-all duration-200',
        isExpanded ? 'shadow-md' : 'hover:shadow-md',
        className
      )}
    >
      {/* Header */}
      <button
        onClick={() => hasRegulatoryDetails && setIsExpanded(!isExpanded)}
        className={cn(
          'w-full p-3 md:p-4 text-left min-h-[48px]',
          hasRegulatoryDetails && 'cursor-pointer'
        )}
        disabled={!hasRegulatoryDetails}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 md:gap-3">
            <div className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
              <User className="h-4 w-4 md:h-5 md:w-5 text-slate-500" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-slate-900">{fullName}</span>
                <span className="text-xs text-slate-400 font-mono bg-slate-100 px-1.5 py-0.5 rounded">
                  {traveler.id}
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700">
                  {formatPassengerType(traveler.passengerTypeCode)}
                </span>
              </div>
            </div>
          </div>

          {hasRegulatoryDetails && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-400 hidden md:inline">
                {traveler.regulatoryDetails!.length} doc{traveler.regulatoryDetails!.length !== 1 ? 's' : ''}
              </span>
              <div className="w-8 h-8 md:w-6 md:h-6 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                {isExpanded ? (
                  <ChevronUp className="h-4 w-4 text-slate-500" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-slate-500" />
                )}
              </div>
            </div>
          )}
        </div>
      </button>

      {/* Expanded content - Regulatory details */}
      {isExpanded && hasRegulatoryDetails && (
        <div className="px-4 pb-4 space-y-3 border-t border-slate-100 pt-3">
          {traveler.regulatoryDetails!.map((detail, idx) => (
            <RegulatoryDetailCard key={detail.id || idx} detail={detail} />
          ))}
        </div>
      )}
    </div>
  );
}

interface RegulatoryDetailCardProps {
  detail: RegulatoryDetail;
}

function RegulatoryDetailCard({ detail }: RegulatoryDetailCardProps) {
  const doc = detail.regulatoryDocument;
  const address = detail.address;

  return (
    <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-xs font-mono text-slate-400">{detail.id}</span>
          {detail.flightIds && detail.flightIds.length > 0 && (
            <div className="flex items-center gap-1">
              {detail.flightIds.map(fid => (
                <span
                  key={fid}
                  className="text-xs font-mono bg-white px-1.5 py-0.5 rounded border border-slate-200"
                >
                  {fid}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">{detail.airlineCode}</span>
          <span className="text-xs text-slate-400 bg-slate-200 px-1.5 py-0.5 rounded">
            {detail.regulatoryApisType.replace('ApisDetail', '')}
          </span>
        </div>
      </div>

      {/* Document info */}
      {doc && (
        <div className="mt-2 pt-2 border-t border-slate-200">
          <InfoGrid columns={2} className="text-xs">
            {doc.number && (
              <InfoItem
                label="Document Number"
                value={<span className="font-mono">{doc.number}</span>}
                monospace
              />
            )}
            {doc.documentType && (
              <InfoItem label="Type" value={doc.documentType} />
            )}
            {doc.expiryDate && (
              <InfoItem
                label="Expiry"
                value={formatDateGerman(doc.expiryDate)}
              />
            )}
            {doc.issuanceCountryCode && (
              <InfoItem label="Issued In" value={doc.issuanceCountryCode} />
            )}
            {doc.nationalityCode && (
              <InfoItem label="Nationality" value={doc.nationalityCode} />
            )}
            {doc.gender && (
              <InfoItem label="Gender" value={formatGender(doc.gender)} />
            )}
            {doc.birthDate && (
              <InfoItem label="Birth Date" value={formatDateGerman(doc.birthDate)} />
            )}
            {doc.name && (
              <InfoItem
                label="Name on Doc"
                value={`${doc.name.firstName} ${doc.name.lastName}`}
              />
            )}
          </InfoGrid>
        </div>
      )}

      {/* Address info */}
      {address && (
        <div className="mt-2 pt-2 border-t border-slate-200">
          <div className="flex items-start gap-2">
            <MapPin className="h-3 w-3 text-slate-400 mt-0.5" />
            <div className="text-xs text-slate-600">
              <div>{address.lines.join(', ')}</div>
              <div>{address.zipCode} {address.cityName}, {address.countryCode}</div>
              <div className="text-slate-400 mt-1">{address.type}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
